import { useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { usePlaceByCoords } from 'hooks/usePlace'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LineKayakIcon from 'LuxKit/Icons/line/LineKayakIcon'
import ExperienceListTileCarousel from 'components/Experiences/ExperienceLists/ExperienceListTileCarousel'
import { formatPlacePhrase } from 'i18n/formatters/placePhrase/formatPlacePhrase'

const EXPERIENCE_LIMIT = 8

/** LE place from port.latitude / port.longitude at city level only. */
const CRUISE_PORT_EXPERIENCE_GEO_LEVELS: Array<App.PlaceType> = ['city']

function parseCoord(value: string | null): number | undefined {
  if (!value) return undefined
  const parsed = parseFloat(value)
  return Number.isNaN(parsed) ? undefined : parsed
}

interface Props {
  port: App.CruisePort | null | undefined
  pageSize?: number
}

function CruiseItineraryExperiences({ port, pageSize = 3 }: Props) {
  const intl = useIntl()
  const latitude = useMemo(() => parseCoord(port?.latitude ?? null), [port?.latitude])
  const longitude = useMemo(() => parseCoord(port?.longitude ?? null), [port?.longitude])

  const [place] = usePlaceByCoords(latitude, longitude, CRUISE_PORT_EXPERIENCE_GEO_LEVELS)
  const placeId = place?.id

  const [list, experiences] = useExperienceSearch(
    {
      placeId,
      limit: EXPERIENCE_LIMIT,
    },
    {},
  )

  const fetching = (!list || list.fetching) && !list?.error

  const displayNameForExperiences = port?.name ?? place?.name ?? ''

  const experiencesSectionTitle = useMemo(
    () =>
      intl.formatMessage(
        {
          id: 'Kz_oSS4elGpnwwQfEVKV',
          defaultMessage: 'Things to do in <emphasis>{placeName}</emphasis>',
          description:
            'Cruise itinerary experiences carousel section title; port or nearby place name is emphasised in italics',
        },
        {
          placeName: formatPlacePhrase({ name: displayNameForExperiences }, intl.locale, 'location'),
          emphasis: (chunks: ReactNode) => <em key="emphasis-place">{chunks}</em>,
        },
      ),
    [intl, displayNameForExperiences],
  )

  const experiencesSectionIntro = useMemo(
    () =>
      intl.formatMessage({
        id: 'fx2FfhsDZafyQEFMVThV',
        defaultMessage:
          'Discover what to see, do, and enjoy near the port during your time ashore.',
        description:
          'Supporting body copy beneath the cruise itinerary experiences heading on a port day',
      }),
    [intl],
  )

  if (!placeId) {
    return null
  }

  if (!fetching && !list?.error && list.ids.length === 0) {
    return null
  }

  return (
    <Group direction="vertical" gap={16}>
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <LineKayakIcon size="S" />
          <Heading variant="heading4">
            {experiencesSectionTitle}
          </Heading>
        </Group>
        <BodyText variant="medium">
          {experiencesSectionIntro}
        </BodyText>
      </Group>
      {!!list?.error && (
        <BodyText variant="large">
          {intl.formatMessage({
            id: '0b-Wid5FhminQdg3Xfzl',
            defaultMessage: 'Something went wrong!',
            description: 'Error message when experience search tile list fails to load',
          })}
        </BodyText>
      )}
      <ExperienceListTileCarousel
        experiences={experiences}
        listFetching={fetching}
        pageSize={pageSize}
        listKey={list.listKey}
      />
    </Group>
  )
}

export default CruiseItineraryExperiences
